import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';

const ExpenseModal = ({ show, onHide, expense, onSave, propertyId }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);

  const formatDateWithContext = (dateString) =>
    formatDate(dateString, { userPreference: user?.dateFormatPreference, agencyCountry });
  const [formData, setFormData] = useState({
    id: null,
    propertyId: propertyId,
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    receipt: '',
    notes: ''
  });

  const expenseCategories = [
    'Maintenance & Repairs',
    'Utilities',
    'Insurance',
    'Property Tax',
    'Cleaning',
    'Landscaping',
    'Security',
    'Legal & Professional',
    'Marketing',
    'Supplies',
    'Other'
  ];

  useEffect(() => {
    if (expense) {
      setFormData({
        ...expense,
        date: expense.date ? expense.date.split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setFormData({
        id: null,
        propertyId: propertyId,
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        receipt: '',
        notes: ''
      });
    }
  }, [expense, propertyId]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const expenseData = {
      ...formData,
      amount: parseFloat(formData.amount),
      id: expense ? expense.id : Date.now(), // Mock ID generation
      createdAt: expense ? expense.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(expenseData);
    onHide();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-receipt me-2"></i>
              {expense ? 'Edit Expense' : 'Add New Expense'}
            </h5>
            <button type="button" className="btn-close" onClick={onHide}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label">Description *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter expense description"
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Amount ({agencyCurrency}) *</label>
                  <input
                    type="number"
                    className="form-control"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Category *</label>
                  <select
                    className="form-select"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select category</option>
                    {expenseCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    onKeyDown={(e) => e.preventDefault()}
                    required
                  />
                  {formData.date && (
                    <small className="form-text fw-semibold text-primary">
                      {formatDateWithContext(formData.date)}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Vendor/Supplier</label>
                  <input
                    type="text"
                    className="form-control"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleChange}
                    placeholder="Enter vendor name"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Receipt/Reference</label>
                  <input
                    type="text"
                    className="form-control"
                    name="receipt"
                    value={formData.receipt}
                    onChange={handleChange}
                    placeholder="Receipt number or reference"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Additional notes about this expense"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-check-circle me-2"></i>
                {expense ? 'Update Expense' : 'Save Expense'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;
